import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Tooltip,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import TuneIcon from "@mui/icons-material/Tune";
import { useTheme } from "@mui/material/styles";
import { useChatSessionsContext } from "../lib/contexts/ChatSessionsContext";

interface ChatFiltersProps {
  isExpanded: boolean;
  onIsExpandedChange: () => void;
}

const ChatFilters: React.FC<ChatFiltersProps> = ({
  isExpanded,
  onIsExpandedChange,
}) => {
  const theme = useTheme(); // Use the useTheme hook to get the theme object
  const {
    filterFromYear,
    setFilterFromYear,
    filterToYear,
    setFilterToYear,
    filterDocumentTypes,
    setFilterDocumentTypes,
    filterAuthor,
    setFilterAuthor,
  } = useChatSessionsContext();

  const DOCUMENT_TYPE_OPTIONS = [
    "publication",
    "grant",
    "patent",
    "patents",
    "popgrant",
  ];

  // const [expanded, setExpanded] = useState<boolean>(isAccordionOpen);
  const [fromYear, setFromYear] = useState<string>(
    filterFromYear ? filterFromYear.toString() : ""
  );
  const [toYear, setToYear] = useState<string>(
    filterToYear ? filterToYear.toString() : ""
  );
  const [docTypes, setDocTypes] = useState<string[]>(filterDocumentTypes ?? []);
  const [author, setAuthor] = useState<string>(filterAuthor ?? "");
  const [fromYearError, setFromYearError] = useState<string | null>(null);
  const [toYearError, setToYearError] = useState<string | null>(null);

  useEffect(() => {
    setFromYear(filterFromYear ? filterFromYear.toString() : "");
  }, [filterFromYear]);

  useEffect(() => {
    setDocTypes(filterDocumentTypes ?? []);
  }, [filterDocumentTypes]);

  useEffect(() => {
    setToYear(filterToYear ? filterToYear.toString() : "");
  }, [filterToYear]);

  useEffect(() => {
    setAuthor(filterAuthor ?? "");
  }, [filterAuthor]);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      onIsExpandedChange();
    };

  const validateYear = (year: string) => {
    if (year === "") return true; // Allow blank year
    const yearRegex = /^\d{4}$/;
    return yearRegex.test(year);
  };

  const commitYearFilters = (): boolean => {
    let valid = true;

    if (!validateYear(fromYear)) {
      setFromYearError("Please enter a valid year in YYYY format");
      valid = false;
    } else if (toYear && fromYear && fromYear >= toYear) {
      setFromYearError("From Year must be less than To Year");
      valid = false;
    } else {
      setFromYearError(null);
      setFilterFromYear(fromYear ? parseInt(fromYear) : null);
    }

    if (!validateYear(toYear)) {
      setToYearError("Please enter a valid year in YYYY format");
      valid = false;
    } else if (fromYear && toYear && fromYear >= toYear) {
      setToYearError("To Year must be greater than From Year");
      valid = false;
    } else {
      setToYearError(null);
      setFilterToYear(toYear ? parseInt(toYear) : null);
    }

    const trimmedAuthor = author.trim();
    setFilterAuthor(trimmedAuthor ? trimmedAuthor : null);

    return valid;
  };

  const handleFromYearBlur = () => {
    commitYearFilters();
  };

  const handleToYearBlur = () => {
    commitYearFilters();
  };

  const handleReset = () => {
    setFromYear(filterFromYear ? filterFromYear.toString() : "");
    setToYear(filterToYear ? filterToYear.toString() : "");
    setFromYearError(null);
    setToYearError(null);
    setFilterFromYear(null);
    setFilterToYear(null);
    setDocTypes([]);
    setFilterDocumentTypes([]);
    setAuthor("");
    setFilterAuthor(null);
  };

  const handleSave = () => {
    if (!commitYearFilters()) {
      return;
    }
    onIsExpandedChange();
  };

  return (
    <Box
      sx={{
        background: "rgba(245, 245, 245, 1)",
        paddingLeft: "5%",
        paddingRight: "5%",
        display: "flex",
        paddingBottom: "1%",
        flexDirection: "column",
        width: "-webkit-fill-available",
        marginTop: "10px",
      }}
    >
      <Accordion
        elevation={0}
        expanded={isExpanded}
        onChange={handleChange("sources")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="sources-content"
          id="sources-header"
          color={"primary"}
          sx={{ backgroundColor: "rgba(245, 245, 245, 1)" }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TuneIcon
              sx={{ marginRight: "5px", color: "#1976D2" }}
              fontSize="inherit"
            />
            <Typography color={"#1976D2"} variant="h6">
              Metadata Filters
              <Tooltip
                placement="right"
                title="Metadata filters will restrict the documents that the large
              language model will consider when generating responses."
              >
                <InfoIcon sx={{ marginLeft: "2px" }} fontSize="inherit" />
              </Tooltip>
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl component="fieldset">
              <FormLabel
                sx={{
                  fontWeight: "bold",
                  marginBottom: theme.spacing(1),
                  padding: theme.spacing(2),
                  paddingLeft: 0,
                  paddingTop: 0,
                }}
                component="legend"
              >
                Publication Year
              </FormLabel>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="From Year"
                  variant="outlined"
                  value={fromYear}
                  onChange={(e) => setFromYear(e.target.value)}
                  onBlur={handleFromYearBlur}
                  placeholder="YYYY"
                  inputProps={{ maxLength: 4 }}
                  error={!!fromYearError}
                  helperText={fromYearError}
                />
                <TextField
                  label="To Year"
                  variant="outlined"
                  value={toYear}
                  onChange={(e) => setToYear(e.target.value)}
                  onBlur={handleToYearBlur}
                  placeholder="YYYY"
                  inputProps={{ maxLength: 4 }}
                  error={!!toYearError}
                  helperText={toYearError}
                />
              </Box>
              <FormLabel
                sx={{
                  fontWeight: "bold",
                  marginBottom: theme.spacing(1),
                  padding: theme.spacing(2),
                  paddingLeft: 0,
                  paddingTop: theme.spacing(3),
                }}
                component="legend"
              >
                Document Type
              </FormLabel>
              <FormControl fullWidth>
                <Select
                  multiple
                  displayEmpty
                  value={docTypes}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    setDocTypes(value);
                    setFilterDocumentTypes(value);
                  }}
                  input={<OutlinedInput />}
                  renderValue={(selected) =>
                    (selected as string[]).length === 0
                      ? "All document types"
                      : (selected as string[]).join(", ")
                  }
                >
                  {DOCUMENT_TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={docTypes.indexOf(type) > -1} />
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormLabel
                sx={{
                  fontWeight: "bold",
                  marginBottom: theme.spacing(1),
                  padding: theme.spacing(2),
                  paddingLeft: 0,
                  paddingTop: theme.spacing(3),
                }}
                component="legend"
              >
                Author
              </FormLabel>
              <FormControl fullWidth>
                <TextField
                  label="Author"
                  variant="outlined"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="e.g. John Smith"
                  InputProps={{
                    endAdornment: (
                      <Tooltip
                        placement="top"
                        title="Case-insensitive match. Multi-word names match regardless of order (e.g. 'john smith' matches 'Smith, John')."
                      >
                        <InfoIcon fontSize="small" sx={{ color: "action.active" }} />
                      </Tooltip>
                    ),
                  }}
                />
              </FormControl>


              <Box
                sx={{ marginTop: theme.spacing(3), display: "flex", gap: 2 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </Box>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ChatFilters;
